import React from 'react';
import { Button, InputNumber, Select } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { SupplierOptionDto } from '../../../dto/reference';
import { POLISH_QUANTITY_INPUT_NUMBER_PROPS } from '../../../utils/polishNumberInput';
import { formatUnit } from '../../../utils/units';
import { useQuantityFormatter } from '../process/OrderProcessTables';

/**
 * „Podziel na dostawców” (decision 2026-10-19-podzial-pozycji-zk-na-dostawcow):
 * one ZK line bought from several suppliers. The parts may stay below the open
 * rest — that rest is ordered later from the same window. The backend
 * (supplierDraftGroupingService.parseSplits) enforces the same rules.
 */
export interface SplitPart { supplierId: string; quantity: number | null }

const EPSILON = 0.0005;
const round = (value: number) => Math.round(value * 1000) / 1000;

export function splitAssigned(parts: SplitPart[]): number {
  return round(parts.reduce((sum, part) => sum + (part.quantity || 0), 0));
}

/** i18n key of the first problem of a split, or null when it can be sent. */
export function splitProblem(parts: SplitPart[], remaining: number): string | null {
  if (parts.length === 0 || parts.some(part => !part.supplierId)) return 'splitSupplierMissing';
  if (parts.some(part => !(Number(part.quantity) > 0))) return 'splitQuantityMissing';
  if (new Set(parts.map(part => part.supplierId)).size !== parts.length) return 'splitDuplicate';
  if (splitAssigned(parts) - remaining > EPSILON) return 'splitTooMuch';
  return null;
}

export default function SupplierLineSplit({ lineName, unit, remaining, parts, suppliers, disabled, onChange }: {
  lineName: string; unit: string; remaining: number; parts: SplitPart[]; suppliers: SupplierOptionDto[];
  disabled: boolean; onChange: (parts: SplitPart[]) => void;
}) {
  const { t, i18n } = useTranslation('orders');
  const quantity = useQuantityFormatter();
  const problem = splitProblem(parts, remaining);
  const rest = round(remaining - splitAssigned(parts));
  const update = (index: number, change: Partial<SplitPart>) => onChange(parts.map((part, at) => at === index ? { ...part, ...change } : part));
  return <div className="supplier-grouping__split" role="group" aria-label={`${t('supplierGrouping.split')} — ${lineName}`}>
    {parts.map((part, index) => <div className="supplier-grouping__split-row" key={index}>
      <Select showSearch optionFilterProp="label" disabled={disabled} placeholder={t('supplierGrouping.choose')}
        aria-label={`${t('supplierGrouping.supplier')} ${index + 1} — ${lineName}`}
        value={part.supplierId || undefined} status={!part.supplierId ? 'warning' : undefined}
        options={suppliers.map(supplier => ({ value: supplier.id, label: supplier.name }))}
        onChange={value => update(index, { supplierId: value })} />
      <InputNumber {...POLISH_QUANTITY_INPUT_NUMBER_PROPS} precision={3} disabled={disabled}
        aria-label={`${t('supplierGrouping.splitQuantity')} ${index + 1} — ${lineName}`}
        value={part.quantity ?? undefined} addonAfter={formatUnit(unit, i18n.language)}
        status={problem === 'splitTooMuch' || !(Number(part.quantity) > 0) ? 'error' : undefined}
        onChange={value => update(index, { quantity: value == null ? null : Number(value) })} />
      <Button type="text" icon={<DeleteOutlined />} disabled={disabled || parts.length === 1}
        aria-label={`${t('supplierGrouping.splitRemove')} ${index + 1} — ${lineName}`}
        onClick={() => onChange(parts.filter((_, at) => at !== index))} />
    </div>)}
    <div className="supplier-grouping__split-footer">
      <Button type="link" size="small" icon={<PlusOutlined />} disabled={disabled}
        onClick={() => onChange([...parts, { supplierId: '', quantity: rest > 0 ? rest : null }])}>{t('supplierGrouping.splitAdd')}</Button>
      <small role={problem ? 'alert' : undefined} className={problem ? 'supplier-grouping__split-error' : undefined}>
        {problem ? t(`supplierGrouping.${problem}`, { quantity: quantity(remaining, unit) })
          : rest > 0 ? t('supplierGrouping.splitRest', { quantity: quantity(rest, unit) }) : t('supplierGrouping.splitDone')}
      </small>
    </div>
  </div>;
}
